// src/components/files/LecteurPdf.tsx — SCE Issues
// Lecteur de PDF de la visionneuse (12/10). Les pages se lisent dans une
// colonne qui défile ; le zoom élargit les pages (elles débordent, on défile
// de côté).
//
// Pourquoi pdf.js et pas un <iframe> : sur iPhone, Safari n'affiche que la
// première page d'un PDF dans un cadre, sans pouvoir défiler. Les pages sont
// donc dessinées une à une dans des <canvas>, et seulement quand elles sont à
// l'écran ou tout près (un PDF de 40 pages ne se dessine pas d'un coup). Une
// page qui s'éloigne rend sa mémoire : zoomée, elle pèse lourd, et l'iPhone
// fermerait l'onglet.
// La version « legacy » de pdf.js tourne aussi sur les Safari un peu anciens.
// Le contenu est lu par Graph (lireContenu) : l'adresse reçue avec la liste
// expire, et SharePoint la sert « à télécharger ».
//
// Zoom (12/10) : − / +, Ctrl + molette ou pincement du pavé tactile sur PC,
// deux doigts sur téléphone. Le point sous les doigts (ou la souris) reste en
// place. Pendant le geste, les pages s'étirent (immédiat) ; elles se
// redessinent nettes dès qu'il s'arrête. La molette seule fait défiler, comme
// dans tout lecteur de PDF.
//
// Tout est chargé À LA DEMANDE : pdf.js (≈ 400 ko) et son worker ne partent
// qu'à l'ouverture d'un PDF. Échec (PDF protégé, abîmé, réseau) : le repli de
// l'appelant (nom du fichier + « Ouvrir dans SharePoint »).

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Loader2, Minus, Plus } from 'lucide-react'
import { lireContenu, type PieceJointe } from '@/api/files.api'
import { chargerPdfjs } from '@/components/files/chargerPdfjs'
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist'

/** Paliers des boutons − / +, en multiples de la largeur de la colonne. */
const PALIERS = [1, 1.5, 2, 3, 4]
const ZOOM_MAX = 4
/** Pas plus large, même sur un grand écran : une page A4 de 1 100 px se lit déjà très bien. */
const LARGEUR_MAX_PX = 1100
/** Au-delà, iOS refuse le <canvas> (≈ 16,7 millions de pixels). */
const PIXELS_MAX = 16_000_000
/** Les pages se redessinent nettes quand le zoom n'a plus bougé depuis… */
const DELAI_RENDU_MS = 180

interface PagePdf {
  page: PDFPageProxy
  /** Hauteur / largeur de la page. */
  ratio: number
}

/** Où remettre le défilement après un zoom : le point sous les doigts reste en place. */
interface Ancre {
  /** Zoom et défilement au début du geste en cours. */
  f0: number
  sl: number
  st: number
  /** Le point fixe, dans la zone visible. */
  fx: number
  fy: number
}

function borner(f: number): number {
  return Math.min(ZOOM_MAX, Math.max(1, f))
}

export function LecteurPdf({
  pj,
  repli,
  margeHaut = 8,
}: {
  pj: PieceJointe
  repli: ReactNode
  /** Place laissée en haut (PDF annoté : le sélecteur Annotée / Originale y est posé). */
  margeHaut?: number
}): JSX.Element {
  const [etat, setEtat] = useState<'chargement' | 'pret' | 'erreur'>('chargement')
  const [pages, setPages] = useState<PagePdf[]>([])
  const [facteur, setFacteur] = useState(1)
  // Le zoom en cours, à jour avant le rendu (plusieurs crans de molette par image).
  const facteurActuel = useRef(1)
  const ancre = useRef<Ancre | null>(null)
  const [largeurColonne, setLargeurColonne] = useState(0)
  const [pageCourante, setPageCourante] = useState(1)
  const defilement = useRef<HTMLDivElement>(null)
  const refsPages = useRef<(HTMLDivElement | null)[]>([])

  // Lecture du document.
  useEffect(() => {
    let annule = false
    let doc: PDFDocumentProxy | null = null
    setEtat('chargement')
    setPages([])
    ;(async () => {
      const [pdfjs, blob] = await Promise.all([chargerPdfjs(), lireContenu(pj)])
      const ouvert = await pdfjs.getDocument({ data: new Uint8Array(await blob.arrayBuffer()) }).promise
      if (annule) {
        void ouvert.destroy()
        return
      }
      doc = ouvert
      const lues: PagePdf[] = []
      for (let i = 1; i <= ouvert.numPages; i++) {
        const page = await ouvert.getPage(i)
        if (annule) return
        const v = page.getViewport({ scale: 1 })
        lues.push({ page, ratio: v.height / v.width })
      }
      setPages(lues)
      setEtat('pret')
    })().catch(() => {
      if (!annule) setEtat('erreur')
    })
    return () => {
      annule = true
      if (doc) void doc.destroy()
    }
    // Un lecteur par pièce jointe (clé posée par l'appelant).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pj.id])

  // Largeur de la colonne : les pages la suivent (rotation, fenêtre redimensionnée).
  useEffect(() => {
    const el = defilement.current
    if (!el) return undefined
    const mesurer = (): void => setLargeurColonne(el.clientWidth)
    mesurer()
    const obs = new ResizeObserver(mesurer)
    obs.observe(el)
    return () => obs.disconnect()
  }, [etat])

  const largeurPage = Math.max(0, Math.min(largeurColonne - 24, LARGEUR_MAX_PX)) * facteur

  /**
   * Zoome vers `voulu`, le point (fx, fy) de la zone visible restant en place.
   * Sans point : le milieu de l'écran (boutons − / +).
   */
  function zoomer(voulu: number, foyer?: { fx: number; fy: number }): void {
    const el = defilement.current
    const nouveau = borner(voulu)
    if (!el || Math.abs(nouveau - facteurActuel.current) < 0.001) return
    const fx = foyer?.fx ?? el.clientWidth / 2
    const fy = foyer?.fy ?? el.clientHeight / 2
    // Plusieurs crans avant le prochain rendu : on garde le départ du premier.
    ancre.current ??= { f0: facteurActuel.current, sl: el.scrollLeft, st: el.scrollTop, fx, fy }
    ancre.current.fx = fx
    ancre.current.fy = fy
    facteurActuel.current = nouveau
    setFacteur(nouveau)
  }
  const zoomerRef = useRef(zoomer)
  zoomerRef.current = zoomer

  // Après le rendu au nouveau zoom : le défilement ramène le point fixe sous les doigts.
  useLayoutEffect(() => {
    const el = defilement.current
    const a = ancre.current
    if (!el || !a) return
    ancre.current = null
    const k = facteur / a.f0
    el.scrollLeft = (a.sl + a.fx) * k - a.fx
    el.scrollTop = (a.st + a.fy) * k - a.fy
  }, [facteur])

  // Gestes : Ctrl + molette (et pincement du pavé tactile, que le navigateur
  // envoie ainsi) sur PC, deux doigts sur téléphone. Écouteurs natifs : il
  // faut pouvoir empêcher le zoom de la page entière.
  useEffect(() => {
    const el = defilement.current
    if (!el || etat !== 'pret') return undefined
    const foyerDe = (x: number, y: number): { fx: number; fy: number } => {
      const r = el.getBoundingClientRect()
      return { fx: x - r.left, fy: y - r.top }
    }
    function molette(e: WheelEvent): void {
      if (!e.ctrlKey) return
      e.preventDefault()
      // Un cran de molette (≈ 100) : ×0,8 ; le pavé tactile envoie de petits pas.
      const vitesse = Math.abs(e.deltaY) > 40 ? 0.002 : 0.01
      zoomerRef.current(facteurActuel.current * Math.exp(-e.deltaY * vitesse), foyerDe(e.clientX, e.clientY))
    }
    let pince: { d0: number; f0: number } | null = null
    const ecart = (t: TouchList): number => Math.hypot(t[0]!.clientX - t[1]!.clientX, t[0]!.clientY - t[1]!.clientY)
    function debut(e: TouchEvent): void {
      if (e.touches.length === 2) pince = { d0: ecart(e.touches) || 1, f0: facteurActuel.current }
    }
    function mouvement(e: TouchEvent): void {
      if (!pince || e.touches.length !== 2) return
      e.preventDefault()
      const [a, b] = [e.touches[0]!, e.touches[1]!]
      zoomerRef.current(pince.f0 * (ecart(e.touches) / pince.d0), foyerDe((a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2))
    }
    function fin(e: TouchEvent): void {
      if (e.touches.length < 2) pince = null
    }
    // Safari (iPhone) : sans cela, il zoome toute la page en même temps.
    const bloquer = (e: Event): void => e.preventDefault()
    el.addEventListener('wheel', molette, { passive: false })
    el.addEventListener('touchstart', debut, { passive: true })
    el.addEventListener('touchmove', mouvement, { passive: false })
    el.addEventListener('touchend', fin)
    el.addEventListener('touchcancel', fin)
    el.addEventListener('gesturestart', bloquer)
    el.addEventListener('gesturechange', bloquer)
    return () => {
      el.removeEventListener('wheel', molette)
      el.removeEventListener('touchstart', debut)
      el.removeEventListener('touchmove', mouvement)
      el.removeEventListener('touchend', fin)
      el.removeEventListener('touchcancel', fin)
      el.removeEventListener('gesturestart', bloquer)
      el.removeEventListener('gesturechange', bloquer)
    }
  }, [etat])

  /** − / + : le palier suivant dans ce sens. */
  function palier(sens: -1 | 1): void {
    const f = facteurActuel.current
    const suivant = sens > 0
      ? PALIERS.find((p) => p > f + 0.01) ?? ZOOM_MAX
      : [...PALIERS].reverse().find((p) => p < f - 0.01) ?? 1
    zoomer(suivant)
  }

  /** Page la plus proche du milieu de la colonne. */
  function suivrePage(): void {
    const el = defilement.current
    if (!el) return
    const milieu = el.scrollTop + el.clientHeight / 2
    let courante = 1
    refsPages.current.forEach((p, i) => {
      if (p && p.offsetTop <= milieu) courante = i + 1
    })
    setPageCourante(courante)
  }

  if (etat === 'erreur') return <>{repli}</>

  const bouton = {
    width: 40,
    height: 40,
    borderRadius: 20,
    border: 'none',
    background: 'transparent',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as const
  const auMin = facteur <= 1.001
  const auMax = facteur >= ZOOM_MAX - 0.001

  return (
    // data-pdf : la scène de la visionneuse laisse ce bloc défiler à sa façon
    // (pas de glissement de photo à photo, pas de zoom de photo).
    <div data-pdf style={{ position: 'absolute', inset: 0 }}>
      <div
        ref={defilement}
        onScroll={suivrePage}
        tabIndex={0}
        role="document"
        aria-label={`PDF ${pj.nom}`}
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'auto',
          padding: `${margeHaut}px 12px 72px`,
          boxSizing: 'border-box',
          background: '#1c1c1c',
          touchAction: 'pan-x pan-y',
          WebkitOverflowScrolling: 'touch',
          outline: 'none',
        }}
      >
        {etat === 'chargement' && (
          <div className="flex items-center justify-center" style={{ height: '100%' }}>
            <Loader2 size={28} className="animate-spin" aria-label="Chargement du PDF" />
          </div>
        )}
        {etat === 'pret' && largeurPage > 0 && (
          // `width: max-content` : la colonne s'élargit avec le zoom, la page
          // reste centrée tant qu'elle tient dans l'écran.
          <div style={{ width: 'max-content', minWidth: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            {pages.map((p, i) => (
              <div
                key={i}
                ref={(el) => {
                  refsPages.current[i] = el
                }}
                style={{ flexShrink: 0 }}
              >
                <PageDessinee page={p.page} largeur={largeurPage} hauteur={largeurPage * p.ratio} rang={i + 1} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Page n / N et zoom, posés sur le bas du lecteur */}
      {etat === 'pret' && (
        <div
          className="flex items-center"
          style={{
            position: 'absolute',
            left: '50%',
            bottom: 14,
            transform: 'translateX(-50%)',
            gap: 2,
            padding: '2px 6px',
            borderRadius: 999,
            background: 'rgba(0,0,0,0.72)',
            color: 'white',
            fontSize: 14,
          }}
        >
          <button
            type="button"
            aria-label="Réduire"
            disabled={auMin}
            onClick={() => palier(-1)}
            style={{ ...bouton, opacity: auMin ? 0.35 : 1 }}
          >
            <Minus size={18} />
          </button>
          <span style={{ minWidth: 92, textAlign: 'center', whiteSpace: 'nowrap' }}>
            {auMin ? `Page ${pageCourante} / ${pages.length}` : `${pageCourante} / ${pages.length} · ${Math.round(facteur * 100)} %`}
          </span>
          <button
            type="button"
            aria-label="Agrandir"
            disabled={auMax}
            onClick={() => palier(1)}
            style={{ ...bouton, opacity: auMax ? 0.35 : 1 }}
          >
            <Plus size={18} />
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Une page : un cadre blanc à sa taille dès le départ (la colonne ne saute pas),
 * dessiné quand il est à l'écran ou tout près, vidé quand il s'en éloigne.
 * Pendant un zoom, le dessin s'étire ; il est refait net à la nouvelle largeur
 * quand le geste s'arrête.
 */
function PageDessinee({
  page,
  largeur,
  hauteur,
  rang,
}: {
  page: PDFPageProxy
  largeur: number
  hauteur: number
  rang: number
}): JSX.Element {
  const cadre = useRef<HTMLDivElement>(null)
  const toile = useRef<HTMLCanvasElement>(null)
  const [proche, setProche] = useState(false)
  const [largeurRendu, setLargeurRendu] = useState(largeur)

  useEffect(() => {
    const el = cadre.current
    if (!el) return undefined
    const obs = new IntersectionObserver(
      (entrees) => {
        const e = entrees[entrees.length - 1]
        if (e) setProche(e.isIntersecting)
      },
      { rootMargin: '900px 0px' },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  // Le zoom bouge encore : on attend qu'il se pose avant de redessiner.
  useEffect(() => {
    if (largeur === largeurRendu) return undefined
    const t = window.setTimeout(() => setLargeurRendu(largeur), DELAI_RENDU_MS)
    return () => window.clearTimeout(t)
  }, [largeur, largeurRendu])

  useEffect(() => {
    const canvas = toile.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx || largeurRendu <= 0) return undefined
    if (!proche) {
      // Loin de l'écran : la mémoire est rendue, le cadre blanc reste.
      canvas.width = 0
      canvas.height = 0
      return undefined
    }
    const base = page.getViewport({ scale: 1 })
    let echelle = (largeurRendu / base.width) * Math.min(2, window.devicePixelRatio || 1)
    const pixels = base.width * echelle * (base.height * echelle)
    if (pixels > PIXELS_MAX) echelle *= Math.sqrt(PIXELS_MAX / pixels)
    const viewport = page.getViewport({ scale: echelle })
    // Dessin hors écran puis copie : l'ancien dessin reste visible jusqu'au bout.
    const brouillon = document.createElement('canvas')
    brouillon.width = Math.floor(viewport.width)
    brouillon.height = Math.floor(viewport.height)
    const ctxBrouillon = brouillon.getContext('2d')
    if (!ctxBrouillon) return undefined
    const tache = page.render({ canvasContext: ctxBrouillon, viewport })
    let fini = false
    tache.promise
      .then(() => {
        fini = true
        canvas.width = brouillon.width
        canvas.height = brouillon.height
        ctx.drawImage(brouillon, 0, 0)
        brouillon.width = 0
        brouillon.height = 0
      })
      // Annulée par un nouveau zoom ou une fermeture : pas une erreur.
      .catch(() => undefined)
    return () => {
      if (!fini) tache.cancel()
    }
  }, [proche, page, largeurRendu])

  return (
    <div
      ref={cadre}
      style={{
        width: largeur,
        height: hauteur,
        background: 'white',
        boxShadow: '0 1px 6px rgba(0,0,0,0.5)',
      }}
    >
      <canvas
        ref={toile}
        role="img"
        aria-label={`Page ${rang}`}
        style={{ display: 'block', width: largeur, height: hauteur }}
      />
    </div>
  )
}
